export const phase4AiActionValues = [
  "phase4.pdf_parse.v1",
  "phase4.master_plan.v1",
  "phase4.target_plan.v1",
  "phase4.item_edit.v1",
  "phase4.master_pdf_merge.v1",
] as const;
export type Phase4AiAction = (typeof phase4AiActionValues)[number];

export const phase5AiActionValues = [
  "phase5.jd_parse.v1",
  "phase5.first_target_from_raw_master.v1",
  "phase5.master_review.v1",
  "phase5.target_review.v1",
  "phase5.review_master_updates.v1",
  "phase5.review_jd_changes.v1",
  "phase5.prompt_review.v1",
  "phase5.section_regenerate.v1",
  "phase5.item_regenerate.v1",
] as const;
export type Phase5AiAction = (typeof phase5AiActionValues)[number];

export const phase6AiActionValues = [
  "phase6.jd_analysis.v1",
  "phase6.jd_proposal_generation.v1",
  "phase6.master_pickup_review.v1",
] as const;
export type Phase6AiAction = (typeof phase6AiActionValues)[number];

export type AiAction = Phase4AiAction | Phase5AiAction | Phase6AiAction;

export const phase4AiTaskTypeValues = [
  "pdf_parse",
  "master_overall_check",
  "target_overall_check",
  "section_item_optimize",
  "master_pdf_merge",
] as const;
export type Phase4AiTaskType = (typeof phase4AiTaskTypeValues)[number];

export const phase5AiTaskTypeValues = phase5AiActionValues;
export type Phase5AiTaskType = (typeof phase5AiTaskTypeValues)[number];

export const phase6AiTaskTypeValues = phase6AiActionValues;
export type Phase6AiTaskType = (typeof phase6AiTaskTypeValues)[number];

export type AiTaskType = Phase4AiTaskType | Phase5AiTaskType | Phase6AiTaskType;

export const phase4ItemEditScopeValues = ["section", "item"] as const;
export type Phase4ItemEditScope = (typeof phase4ItemEditScopeValues)[number];

export const quotaLedgerEntryTypeValues = [
  "free_grant",
  "purchase_grant",
  "manual_grant",
  "manual_debit",
  "usage_reserve",
  "usage_commit",
  "usage_release",
  "usage_reverse",
  "refund_debit",
  "chargeback_debit",
  "expiration",
  "correction",
] as const;
export type QuotaLedgerEntryType = (typeof quotaLedgerEntryTypeValues)[number];

export const quotaLedgerBucketValues = ["free", "support", "paid", "paid_equivalent", "internal"] as const;
export type QuotaLedgerBucket = (typeof quotaLedgerBucketValues)[number];

export const quotaReservationStatusValues = [
  "reserved",
  "committed",
  "released",
  "reversed",
  "expired",
  "needs_review",
] as const;
export type QuotaReservationStatus = (typeof quotaReservationStatusValues)[number];

export const quotaAiJobStatusValues = ["reserved", "running", "succeeded", "failed", "canceled", "released"] as const;
export type QuotaAiJobStatus = (typeof quotaAiJobStatusValues)[number];

export type QuotaMetadataValue = boolean | number | string | null | undefined;
export type QuotaMetadata = Record<string, QuotaMetadataValue>;

export type QuotaLedgerEntryRecord = {
  id: string;
  userId: string;
  entryType: QuotaLedgerEntryType;
  bucket: QuotaLedgerBucket;
  amount: number;
  reservedAmount: number;
  committedAmount: number;
  sourceType: string;
  sourceId: string;
  orderId?: string | null;
  paymentEventId?: string | null;
  adjustmentId?: string | null;
  reservationId?: string | null;
  aiJobId?: string | null;
  reasonCode: string;
  idempotencyKey: string;
  balanceAfter?: number | null;
  expiresAt?: Date | null;
  userVisibleNote?: string | null;
  internalNote?: string | null;
  createdById?: string | null;
  createdAt: Date;
};

export type QuotaReservationRecord = {
  id: string;
  userId: string;
  action: AiAction;
  requiredUnits: number;
  status: QuotaReservationStatus;
  idempotencyKey: string;
  reservedAt?: Date | null;
  committedAt?: Date | null;
  releasedAt?: Date | null;
  reversedAt?: Date | null;
  expiresAt: Date;
  metadata?: QuotaMetadata | null;
  createdAt: Date;
  updatedAt: Date;
};

export type QuotaAiJobRecord = {
  id: string;
  userId: string;
  taskType: AiTaskType;
  action: AiAction;
  status: QuotaAiJobStatus;
  quotaRequired: number;
  quotaReserved: number;
  quotaCommitted: number;
  reservationId?: string | null;
  requestId: string;
  idempotencyKey: string;
  provider?: string | null;
  model?: string | null;
  workflowVersion?: string | null;
  promptVersion?: string | null;
  modelVersion?: string | null;
  schemaVersion?: string | null;
  resumeId?: string | null;
  targetResumeId?: string | null;
  sectionType?: string | null;
  failureCode?: string | null;
  errorType?: string | null;
  metadata?: Record<string, unknown> | null;
  startedAt?: Date | null;
  completedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

export type AiJobVersionTuple = {
  workflowVersion: string | null;
  promptVersion: string | null;
  modelVersion: string | null;
  schemaVersion: string | null;
};

export type QuotaBalance = {
  availableUnits: number;
  paidUnits: number;
  freeOrSupportUnits: number;
};

export type QuotaLifecycleState =
  | "allowed"
  | "insufficient_quota"
  | "paid_ai_disabled"
  | "account_suspended"
  | "chargeback_hold"
  | "needs_review";

export type QuotaActionContext = QuotaMetadata & {
  resumeId?: string;
  targetResumeId?: string;
  sectionType?: string;
  requestId?: string;
  provider?: string;
  model?: string;
  workflowVersion?: string;
  modelVersion?: string;
  promptVersion?: string;
  schemaVersion?: string;
  scope?: Phase4ItemEditScope;
  subAction?: string;
};

const actionToTaskType: Record<Phase4AiAction, Phase4AiTaskType> = {
  "phase4.pdf_parse.v1": "pdf_parse",
  "phase4.master_plan.v1": "master_overall_check",
  "phase4.target_plan.v1": "target_overall_check",
  "phase4.item_edit.v1": "section_item_optimize",
  "phase4.master_pdf_merge.v1": "master_pdf_merge",
};

const phase5ActionToTaskType: Record<Phase5AiAction, Phase5AiTaskType> = Object.fromEntries(
  phase5AiActionValues.map((action) => [action, action]),
) as Record<Phase5AiAction, Phase5AiTaskType>;

const phase6ActionToTaskType: Record<Phase6AiAction, Phase6AiTaskType> = Object.fromEntries(
  phase6AiActionValues.map((action) => [action, action]),
) as Record<Phase6AiAction, Phase6AiTaskType>;

const sensitiveMetadataKeyPattern =
  /raw|pdf[-_ ]?text|resume[-_ ]?(data|body|text)|jd[-_ ]?text|prompt|provider[-_ ]?response|response|content|description|file[-_ ]?text/i;

export function getPhase4AiTaskType(action: Phase4AiAction) {
  return actionToTaskType[action];
}

export function getAiTaskType(action: AiAction): AiTaskType {
  if (action.startsWith("phase6.")) return phase6ActionToTaskType[action as Phase6AiAction];
  if (action.startsWith("phase5.")) return phase5ActionToTaskType[action as Phase5AiAction];

  return actionToTaskType[action as Phase4AiAction];
}

function readMetadataVersionTuple(metadata: Record<string, unknown> | null | undefined): Partial<AiJobVersionTuple> {
  const tuple = metadata?.versionTuple;
  if (!tuple || typeof tuple !== "object" || Array.isArray(tuple)) return {};

  const record = tuple as Record<string, unknown>;

  return {
    workflowVersion: typeof record.workflowVersion === "string" ? record.workflowVersion : null,
    promptVersion: typeof record.promptVersion === "string" ? record.promptVersion : null,
    modelVersion: typeof record.modelVersion === "string" ? record.modelVersion : null,
    schemaVersion: typeof record.schemaVersion === "string" ? record.schemaVersion : null,
  };
}

export function getAiJobVersionTuple(
  job: Partial<Pick<QuotaAiJobRecord, "workflowVersion" | "promptVersion" | "modelVersion" | "schemaVersion">> & {
    metadata?: Record<string, unknown> | null;
  },
): AiJobVersionTuple {
  const metadataTuple = readMetadataVersionTuple(job.metadata);

  return {
    workflowVersion: job.workflowVersion ?? metadataTuple.workflowVersion ?? null,
    promptVersion: job.promptVersion ?? metadataTuple.promptVersion ?? null,
    modelVersion: job.modelVersion ?? metadataTuple.modelVersion ?? null,
    schemaVersion: job.schemaVersion ?? metadataTuple.schemaVersion ?? null,
  };
}

export function sanitizeQuotaMetadata(metadata: QuotaMetadata = {}): QuotaMetadata {
  const safeVersionKeyPattern = /^(?:onlineCheck)?(?:Prompt|Model|Schema|Workflow)Version$/;
  return Object.fromEntries(
    Object.entries(metadata).filter(([key, value]) => {
      if (value === undefined) return false;
      if (key === "quotaSourceExpiresAt" || key === "quotaSourceAllocationV1") return false;
      if (sensitiveMetadataKeyPattern.test(key) && !safeVersionKeyPattern.test(key)) return false;
      return typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value === null;
    }),
  );
}

export function mergeAiJobMetadataForTransition(
  existing: Record<string, unknown> | null | undefined,
  transition: QuotaMetadata,
) {
  return { ...existing, ...transition };
}
